import React from "react";
import { Link, useParams } from "react-router-dom";
import SEO from "../components/SEO";
import Header from "../components/Header";
import HeroSection from "../components/HeroSection";
import ServiceHighlights from "../components/ServiceHighlights";
import ServicesOverview from "../components/ServicesOverview";
import FAQ from "../components/FAQ";
import Footer from "../components/Footer";
import PartnerMarquee from "../components/PartnerMarquee";
import HelpButton from "../components/HelpButton";
import { generateLocalBusinessSchema } from "../lib/schema";
import { Phone } from "lucide-react";

// Fixed areas that are common to all cities
const commonAreas = [
  "Centrum",
  "Noord",
  "Zuid",
  "Oost",
  "West",
  "Bedrijventerreinen",
  "Industriegebied",
  "Buitengebied",
];

// Additional areas specific to each city
const citySpecificAreas: { [key: string]: string[] } = {
  hoorn: [
    "Binnenstad",
    "Venenlaankwartier",
    "Hoorn-Noord",
    "Nieuwe Steen",
    "Risdam-Zuid",
    "Risdam-Noord",
    "Kersenboogerd",
    "Blokker",
    "Zwaag",
  ],
};

// Function to format city name for display
function formatCityName(name: string) {
  return name
    .replace(/-/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function RegioDetail() {
  const { city } = useParams();
  const formattedCity = formatCityName(city || "");
  const normalizedCity = (city || "").toLowerCase();

  // Get areas for this city
  const areas = [
    ...commonAreas.map((area) => `${formattedCity} ${area}`),
    ...(citySpecificAreas[normalizedCity] || []),
  ];

  const seoData = {
    title: `Elektricien in ${formattedCity} | elektricieninarnhem026.nl`,
    description: `Uw elektricien in ${formattedCity}. Lokale expertise en snelle service.`,
    schema: generateLocalBusinessSchema(formattedCity),
  };

  return (
    <div className="min-h-screen bg-white page-transition pt-[99px] md:pt-[133px]">
      <SEO {...seoData} />
      <Header />
      <HeroSection
        title={`Elektricien in ${formattedCity}`}
        description={`Elektricien in ${formattedCity} en omgeving. Vraag vrijblijvend een offerte aan!`}
        imageSrc="/images/mirt2.png"
      />
      <ServiceHighlights />

      <section className="mx-auto w-[calc(100%-2.5rem)] max-w-[1088px] mb-12 md:mb-16">
        <div className="mb-12">
          <h2 className="font-bold text-[24px] lg:text-[36px] text-[#17354A] mb-6">
            Werkgebied {formattedCity}
          </h2>
          <div className="flex flex-wrap gap-3">
            {areas.map((area, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-[#F8F9FB] rounded-lg text-[#17354A]"
              >
                {area}
              </span>
            ))}
          </div>
        </div>

        <div className="grid items-center w-full gap-12 grid-cols-1 md:grid-cols-2 mb-16 md:mb-20 xl:mb-32">
          <div className="[&_p]:!text-[#17354A] [&_p]:text-base md:[&_p]:text-lg order-2 md:order-none">
            <h2 className="font-bold text-black leading-[1.25] text-[24px] lg:text-[36px] text-[#17354A] mb-4 md:mb-6">
              Uw vertrouwde elektricien met specialistische kennis in{" "}
              {formattedCity}
            </h2>
            <p>
              Als lokale elektricien met uitgebreide ervaring in {formattedCity}
              , bieden wij betrouwbare oplossingen voor al uw elektrische
              installaties en reparaties. Of het nu gaat om het installeren van
              nieuwe systemen, het updaten van verouderde bedrading, of het
              uitvoeren van veiligheidscontroles, wij zorgen voor een veilige en
              efficiënte uitvoering van het werk.
            </p>

            <div className="h-9"></div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/offerte"
                aria-label="Vraag een vrijblijvende offerte aan voor groepenkast vervanging"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-transparent items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] bg-[#F47B20] text-white hover:bg-[#F47B20]/80 focus:bg-[#F47B20]/80 focus-visible:border-crustaLight disabled:bg-[#F47B20]/40 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">
                  Offerte aanvragen
                </span>
              </Link>

              <a
                href="tel:+31852129778"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-[#17354A] items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] text-[#17354A] hover:bg-[#E6B800] hover:text-white focus:opacity-60 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  Bel direct 085-2129778
                </span>
              </a>
            </div>
          </div>
          <div className="order-1 md:-order-1">
            <div className="relative">
              <img
                alt={`Elektricien in ${formattedCity}`}
                loading="lazy"
                className="rounded-lg w-full h-auto"
                src="/images/mark.png"
              />
            </div>
          </div>
        </div>

        {/* Services Grid */}
      </section>

      <ServicesOverview />
      <FAQ />

      <PartnerMarquee />

      <div className="w-full cursor-pointer bg-[#FFD100] transition-opacity duration-150 hover:opacity-80">
        <Link
          to="/offerte"
          aria-label="Vraag een vrijblijvende offerte aan voor groepenkast vervanging"
        >
          <section className="mx-auto w-[calc(100%-2.5rem)] max-w-[1088px] text-center py-6 justify-between items-center">
            <div
              data-variant="h2"
              className="font-bold leading-[1.25] text-[28px] lg:text-[36px] self-center text-white mb-0"
            >
              Offerte aanvragen
            </div>
          </section>
        </Link>
      </div>

      <HelpButton />
      <Footer />
    </div>
  );
}

export default RegioDetail;
